"use client";

import "@assistant-ui/react-markdown/styles/dot.css";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock
} from "@assistant-ui/react-markdown";
import { useAuiState } from "@assistant-ui/react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfmStatic from "remark-gfm";
import remarkGfm from "remark-gfm-react-10";
import { type FC, memo, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { TooltipIconButton } from "@/agents-v2/components/assistant-ui/tooltip-icon-button";
import { parseMessageSources } from "@/agents-v2/utils/messageSources";
import { cn } from "@/agents-v2/utils/cn";

// Protocols react-markdown's defaultUrlTransform keeps; everything else is
// stripped to "". Mirrored here so our urlTransform can replicate the default
// sanitisation while also allowing our internal source scheme.
const SAFE_URL_PROTOCOL = /^(https?|ircs?|mailto|xmpp)$/i;

/**
 * URL sanitiser for the markdown renderer. Behaves like react-markdown's
 * `defaultUrlTransform` but also preserves `mytender-source:` citation links —
 * the default strips them to "" (its scheme isn't in the safe list), which turns
 * a clicked source link into an `href=""` page reload instead of opening the
 * source popup.
 * @param value - The raw URL from a markdown link/image.
 * @returns The URL when safe (or a source link), otherwise an empty string.
 */
export const sourceAwareUrlTransform = (value: string): string => {
  if (value.startsWith("mytender-source:")) return value;
  const colon = value.indexOf(":");
  const questionMark = value.indexOf("?");
  const numberSign = value.indexOf("#");
  const slash = value.indexOf("/");
  if (
    colon === -1 ||
    (slash !== -1 && colon > slash) ||
    (questionMark !== -1 && colon > questionMark) ||
    (numberSign !== -1 && colon > numberSign) ||
    SAFE_URL_PROTOCOL.test(value.slice(0, colon))
  ) {
    return value;
  }
  return "";
};

/**
 * Removes the trailing "Sources" citation block from a message part before it's
 * rendered as Markdown — the sources are surfaced separately as a hover next to
 * the message action bar (see MessageSources), not inline in the answer body.
 * @param text - The raw Markdown for the part.
 * @returns The body with any trailing sources section stripped.
 */
export const stripSourcesSection = (text: string): string =>
  parseMessageSources(text).body;

const MarkdownTextImpl = () => {
  const partType = useAuiState((state) => state.part.type);
  if (partType !== "text" && partType !== "reasoning") return null;

  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm as never]}
      urlTransform={sourceAwareUrlTransform}
      preprocess={stripSourcesSection}
      className="aui-md max-w-none text-base leading-relaxed text-foreground"
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const staticMarkdownComponents: Components = {
  h1: ({ node: _node, className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mb-3 mt-5 scroll-m-20 text-xl font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h2: ({ node: _node, className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mb-2.5 mt-5 scroll-m-20 text-lg font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h3: ({ node: _node, className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mb-2 mt-4 scroll-m-20 text-base font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h4: ({ node: _node, className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mb-1.5 mt-3 scroll-m-20 text-base font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h5: ({ node: _node, className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 mb-1.5 mt-3 text-base font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h6: ({ node: _node, className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 mb-1.5 mt-3 text-base font-semibold leading-snug text-muted-foreground first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  p: ({ node: _node, className, ...props }) => (
    <p
      className={cn(
        "aui-md-p my-2.5 leading-relaxed first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  a: ({ node: _node, className, href, onClick, ...props }) => (
    <a
      className={cn(
        "aui-md-a break-words text-primary underline underline-offset-2 hover:text-primary/80",
        className
      )}
      href={href}
      onClick={(event) => {
        if (href?.startsWith("mytender-source:")) {
          event.preventDefault();
          window.dispatchEvent(
            new CustomEvent("agents:open-source", { detail: { href } })
          );
        }
        onClick?.(event);
      }}
      {...props}
    />
  ),
  blockquote: ({ node: _node, className, ...props }) => (
    <blockquote
      className={cn(
        "aui-md-blockquote my-2.5 border-muted-foreground/30 border-s-2 ps-3 text-muted-foreground italic",
        className
      )}
      {...props}
    />
  ),
  ul: ({ node: _node, className, ...props }) => (
    <ul
      className={cn(
        "aui-md-ul my-2.5 ms-5 list-disc marker:text-muted-foreground [&>li]:mt-1.5",
        className
      )}
      {...props}
    />
  ),
  ol: ({ node: _node, className, ...props }) => (
    <ol
      className={cn(
        "aui-md-ol my-2.5 ms-5 list-decimal marker:text-muted-foreground [&>li]:mt-1.5",
        className
      )}
      {...props}
    />
  ),
  li: ({ node: _node, className, ...props }) => (
    <li className={cn("aui-md-li leading-relaxed", className)} {...props} />
  ),
  table: ({ node: _node, className, ...props }) => (
    <table
      className={cn(
        "aui-md-table my-2 w-full border-separate border-spacing-0 overflow-y-auto",
        className
      )}
      {...props}
    />
  ),
  th: ({ node: _node, className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-2 py-1 text-start font-medium first:rounded-ss-lg last:rounded-se-lg [[align=center]]:text-center [[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  td: ({ node: _node, className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-muted-foreground/20 border-s border-b px-2 py-1 text-start last:border-e [[align=center]]:text-center [[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  tr: ({ node: _node, className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-es-lg [&:last-child>td:last-child]:rounded-ee-lg",
        className
      )}
      {...props}
    />
  ),
  pre: ({ node: _node, className, ...props }) => (
    <pre
      className={cn(
        "aui-md-pre overflow-x-auto rounded-lg border border-border/50 bg-muted/30 p-3 text-xs leading-relaxed",
        className
      )}
      {...props}
    />
  ),
  code: ({ node: _node, inline, className, ...props }) => (
    <code
      className={cn(
        inline
          ? "aui-md-inline-code rounded-md border border-border/50 bg-muted/50 px-1.5 py-0.5 font-mono text-[0.85em]"
          : "aui-md-code font-mono",
        className
      )}
      {...props}
    />
  )
};

export const MarkdownContent = memo(function MarkdownContent({
  content,
  className
}: {
  content: string;
  className?: string;
}) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfmStatic]}
      transformLinkUri={sourceAwareUrlTransform}
      className={cn(
        "aui-md max-w-none text-sm leading-relaxed text-foreground",
        className
      )}
      components={staticMarkdownComponents}
    >
      {stripSourcesSection(content)}
    </ReactMarkdown>
  );
});

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="aui-code-header-root mt-2.5 flex items-center justify-between rounded-t-lg border border-border/50 border-b-0 bg-muted/50 px-3 py-1.5 text-xs">
      <span className="aui-code-header-language font-medium text-muted-foreground lowercase">
        {language}
      </span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = async (value: string) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    } catch (error) {
      console.error("Failed to copy text to clipboard", error);
    }
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mb-3 mt-5 scroll-m-20 text-xl font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mb-2.5 mt-5 scroll-m-20 text-lg font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mb-2 mt-4 scroll-m-20 text-base font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mb-1.5 mt-3 scroll-m-20 text-base font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 mb-1.5 mt-3 text-base font-semibold leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 mb-1.5 mt-3 text-base font-semibold leading-snug text-muted-foreground first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "aui-md-p my-2.5 leading-relaxed first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  a: ({ className, href, onClick, ...props }) => (
    <a
      className={cn(
        "aui-md-a text-primary underline underline-offset-2 hover:text-primary/80",
        className
      )}
      href={href}
      onClick={(event) => {
        if (href?.startsWith("mytender-source:")) {
          event.preventDefault();
          window.dispatchEvent(
            new CustomEvent("agents:open-source", { detail: { href } })
          );
        }
        onClick?.(event);
      }}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn(
        "aui-md-blockquote my-2.5 border-muted-foreground/30 border-s-2 ps-3 text-muted-foreground italic",
        className
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn(
        "aui-md-ul my-2.5 ms-5 list-disc marker:text-muted-foreground [&>li]:mt-1.5",
        className
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn(
        "aui-md-ol my-2.5 ms-5 list-decimal marker:text-muted-foreground [&>li]:mt-1.5",
        className
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr
      className={cn("aui-md-hr my-2 border-muted-foreground/20", className)}
      {...props}
    />
  ),
  table: ({ className, ...props }) => (
    <table
      className={cn(
        "aui-md-table my-2 w-full border-separate border-spacing-0 overflow-y-auto",
        className
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-2 py-1 text-start font-medium first:rounded-ss-lg last:rounded-se-lg [[align=center]]:text-center [[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-muted-foreground/20 border-s border-b px-2 py-1 text-start last:border-e [[align=center]]:text-center [[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-es-lg [&:last-child>td:last-child]:rounded-ee-lg",
        className
      )}
      {...props}
    />
  ),
  li: ({ className, ...props }) => (
    <li className={cn("aui-md-li leading-relaxed", className)} {...props} />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn("aui-md-sup [&>a]:text-xs [&>a]:no-underline", className)}
      {...props}
    />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "aui-md-pre overflow-x-auto rounded-t-none rounded-b-lg border border-border/50 border-t-0 bg-muted/30 p-3 text-xs leading-relaxed",
        className
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock &&
            "aui-md-inline-code rounded-md border border-border/50 bg-muted/50 px-1.5 py-0.5 font-mono text-[0.85em]",
          className
        )}
        {...props}
      />
    );
  },
  CodeHeader
});
